import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DensityMode = 'comfortable' | 'compact';

export interface CommandPaletteState {
  isOpen: boolean;
  query: string;
  initialAction: string;
}

export interface UiState {
  windowWidth: number;
  isTicketPanelOpen: boolean;
  isLoading: boolean;
  density: DensityMode;
  commandPalette: CommandPaletteState;
}

const initialState: UiState = {
  windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
  isTicketPanelOpen: false,
  isLoading: true,
  density: 'comfortable',
  commandPalette: {
    isOpen: false,
    query: '',
    initialAction: ''
  }
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openCommandPalette: {
      reducer(state, action: PayloadAction<string>) {
        state.commandPalette.isOpen = true;
        state.commandPalette.initialAction = action.payload;
      },
      // Optional payload so existing no-arg dispatches keep compiling; the
      // palette otherwise never had initialAction written by any reducer.
      prepare(initialAction?: string) {
        return { payload: initialAction ?? '' };
      },
    },
    closeCommandPalette(state) {
      state.commandPalette.isOpen = false;
      // Reset the transient fields: reopening the palette used to surface the
      // previous session's typed query and pre-selected action.
      state.commandPalette.query = '';
      state.commandPalette.initialAction = '';
    },
    setCommandPaletteQuery(state, action: PayloadAction<string>) {
      state.commandPalette.query = action.payload;
    },
    toggleDensity(state) {
      state.density = state.density === 'compact' ? 'comfortable' : 'compact';
    },
    setDensity(state, action: PayloadAction<DensityMode>) {
      state.density = action.payload;
    },
    toggleTicketPanel: (state) => {
      state.isTicketPanelOpen = !state.isTicketPanelOpen;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateWindowWidth: (state, action: PayloadAction<number>) => {
      state.windowWidth = action.payload;
      if (state.windowWidth > 992) {
        state.isTicketPanelOpen = false;
      }
    },
  },
});

export const { toggleTicketPanel, setLoading, updateWindowWidth, openCommandPalette, closeCommandPalette, setCommandPaletteQuery, toggleDensity, setDensity } = uiSlice.actions;

export const selectWindowWidth = (state: { ui: UiState }) => state.ui.windowWidth;
export const selectIsTicketPanelOpen = (state: { ui: UiState }) => state.ui.isTicketPanelOpen;
export const selectIsLoading = (state: { ui: UiState }) => state.ui.isLoading;
export const selectIsMobileView = (state: { ui: UiState }) => state.ui.windowWidth <= 992;
export const selectIsTabletView = (state: { ui: UiState }) => state.ui.windowWidth > 992 && state.ui.windowWidth <= 1200;

export default uiSlice.reducer;
